import React from 'react';

// ─────────────────────────────────────────────
//  LeoMascot — now using the new PNG character
//  All exports kept identical so no other files
//  need changing.
// ─────────────────────────────────────────────

const MOOD_EMOJI = {
  happy:       null,
  celebrating: '🎉',
  sad:         '😢',
  thinking:    '🤔',
  excited:     '🤩',
  sleepy:      '😴',
};

const PROP_EMOJI = {
  wave:       '👋',
  book:       '📖',
  paintbrush: '🎨',
  headset:    '🎧',
  star:       '⭐',
};

// ─────────────────────────────────────────────
//  LeoFace — small avatar used in header & nav
// ─────────────────────────────────────────────
export function LeoFace({ mood = 'happy', size = 48, className = '' }) {
  const emoji = MOOD_EMOJI[mood];
  return (
    <div
      className={className}
      style={{
        width: size,
        height: size,
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        overflow: 'hidden',
        borderRadius: '50%',
      }}
      aria-label={`Leo is ${mood}`}
    >
      <img
        src="/images/leo-mascot.png"
        alt="Leo the Lion Cub"
        style={{
          width: size * 1.8,
          height: size * 1.8,
          objectFit: 'cover',
          objectPosition: 'center 8%',
          position: 'absolute',
          top: -size * 0.2,
          left: -size * 0.4,
        }}
      />
      {emoji && (
        <span
          style={{
            position: 'absolute',
            bottom: -2,
            right: -2,
            fontSize: size * 0.35,
            lineHeight: 1,
            filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
            zIndex: 2,
          }}
        >
          {emoji}
        </span>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
//  LeoFullBody — hero mascot, full image
// ─────────────────────────────────────────────
export function LeoFullBody({ mood = 'happy', className = '' }) {
  const emoji = MOOD_EMOJI[mood];
  return (
    <div
      className={className}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
      }}
      aria-label="Leo the Lion Cub"
    >
      <img
        src="/images/leo-mascot.png"
        alt="Leo the Lion Cub"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          filter: 'drop-shadow(0 12px 32px rgba(0,0,0,0.18))',
        }}
      />
      {emoji && (
        <span
          style={{
            position: 'absolute',
            top: '5%',
            right: '5%',
            fontSize: '2rem',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
          }}
        >
          {emoji}
        </span>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
//  LeoWithProp — Leo with an item/accessory
// ─────────────────────────────────────────────
export function LeoWithProp({ prop = 'wave', mood = 'happy', className = '' }) {
  const propEmoji = PROP_EMOJI[prop] || '👋';
  const moodEmoji = MOOD_EMOJI[mood];
  return (
    <div
      className={className}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
      }}
      aria-label={`Leo with ${prop}`}
    >
      <img
        src="/images/leo-mascot.png"
        alt="Leo the Lion Cub"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.15))',
        }}
      />
      <span
        style={{
          position: 'absolute',
          bottom: '15%',
          right: '0%',
          fontSize: '2.2rem',
          filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.25))',
        }}
      >
        {propEmoji}
      </span>
      {moodEmoji && (
        <span
          style={{
            position: 'absolute',
            top: '5%',
            right: '5%',
            fontSize: '1.5rem',
          }}
        >
          {moodEmoji}
        </span>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
//  LeoSticker — small badge/reward version
// ─────────────────────────────────────────────
export function LeoSticker({ mood = 'happy', size = 80, className = '' }) {
  const emoji = MOOD_EMOJI[mood];
  return (
    <div
      className={className}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.3)',
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        flexShrink: 0,
      }}
      aria-label={`Leo sticker: ${mood}`}
    >
      <img
        src="/images/leo-mascot.png"
        alt="Leo"
        style={{
          width: size * 1.5,
          height: size * 1.5,
          objectFit: 'cover',
          objectPosition: 'center 10%',
          position: 'absolute',
          top: -size * 0.1,
          left: -size * 0.25,
        }}
      />
      {emoji && (
        <span
          style={{
            position: 'absolute',
            bottom: 2,
            right: 2,
            fontSize: size * 0.28,
            lineHeight: 1,
            zIndex: 2,
          }}
        >
          {emoji}
        </span>
      )}
    </div>
  );
}

export default LeoFace;
